// src/Dashboard/pages/user/DashboardUser/components/MessagesPanel.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCheckDouble,
  FaEnvelope,
  FaEye,
  FaPaperPlane,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  deleteMessageById,
  getMessagesByUser,
  markAllMessagesAsRead,
  markMessageAsRead,
} from "../../../../../services/messageServices";
import { useMessage } from "../../../../../hooks/MessageContext";

import DashboardDrawer from "./DashboardDrawer";
import EmptyState from "./EmptyState";
import {
  DASHBOARD_ROUTES,
  formatDateTimeFR,
  isReadFlag,
  sortByDateDesc,
} from "../utils/dashboardUserUtils";

export default function MessagesPanel({ userId, onCounts }) {
  const navigate = useNavigate();
  const { setUnreadMessageCount, refresh } = useMessage();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const prevCountsRef = useRef({ total: -1, unread: -1 });

  const computeCounts = useCallback((list) => {
    const total = list.length;
    const unread = list.reduce(
      (acc, message) => acc + (isReadFlag(message.read_message) ? 0 : 1),
      0
    );

    return { total, unread };
  }, []);

  const pushCounts = useCallback(
    (list) => {
      const next = computeCounts(list);
      const prev = prevCountsRef.current;

      if (prev.total !== next.total || prev.unread !== next.unread) {
        prevCountsRef.current = next;
        onCounts?.(next);
        setUnreadMessageCount?.(next.unread);
      }
    },
    [computeCounts, onCounts, setUnreadMessageCount]
  );

  const load = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);

      const data = await getMessagesByUser(userId);
      const list = sortByDateDesc(
        (Array.isArray(data) ? data : []).filter((message) => !message.is_copy),
        "created_at"
      );

      setMessages(list);
      pushCounts(list);
    } catch (error) {
      console.error("[MessagesPanel] load error:", error);
      toast.error("Impossible de charger les messages.");
    } finally {
      setLoading(false);
    }
  }, [userId, pushCounts]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => computeCounts(messages), [messages, computeCounts]);

  const openMessage = async (message) => {
    setSelectedMessage(message);

    const unread = !isReadFlag(message.read_message);
    if (!unread) return;

    try {
      await markMessageAsRead(message.id);

      const next = messages.map((item) =>
        item.id === message.id ? { ...item, read_message: true } : item
      );

      setMessages(next);
      pushCounts(next);
      refresh?.();
      setSelectedMessage({ ...message, read_message: true });
    } catch (error) {
      console.error("[MessagesPanel] mark read error:", error);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm("Supprimer ce message ?")) return;

    try {
      await deleteMessageById(messageId);

      const next = messages.filter((message) => message.id !== messageId);
      setMessages(next);
      pushCounts(next);
      setSelectedMessage(null);

      toast.success("Message supprimé.");
    } catch (error) {
      console.error("[MessagesPanel] delete error:", error);
      toast.error("Impossible de supprimer le message.");
    }
  };

  const markAllRead = async () => {
    try {
      await markAllMessagesAsRead(userId);

      const next = messages.map((message) => ({
        ...message,
        read_message: true,
      }));

      setMessages(next);
      pushCounts(next);
      refresh?.();

      toast.success("Tous les messages ont été marqués comme lus.");
    } catch (error) {
      console.error("[MessagesPanel] mark all read error:", error);
      toast.error("Impossible de marquer les messages comme lus.");
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  if (!messages.length) {
    return (
      <EmptyState
        icon={FaEnvelope}
        title="Aucun message"
        description="Vos messages privés apparaîtront ici."
        action={
          <button
            type="button"
            onClick={() => navigate(DASHBOARD_ROUTES.messaging)}
            className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700"
          >
            <FaPaperPlane />
            Ouvrir la messagerie
          </button>
        }
      />
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-slate-600 dark:text-slate-300">
          <span className="font-bold text-slate-900 dark:text-white">
            {counts.unread}
          </span>{" "}
          message{counts.unread > 1 ? "s" : ""} non lu
          {counts.unread > 1 ? "s" : ""} sur {counts.total}.
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={markAllRead}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaCheckDouble />
            Tout marquer lu
          </button>

          <button
            type="button"
            onClick={() => navigate(DASHBOARD_ROUTES.messaging)}
            className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-3 py-2 text-xs font-bold text-white hover:bg-violet-700"
          >
            <FaPaperPlane />
            Messagerie complète
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {messages.map((message) => {
          const unread = !isReadFlag(message.read_message);
          const { date, time } = formatDateTimeFR(
            message.created_at || message.createdAt
          );

          return (
            <article
              key={message.id}
              className={[
                "rounded-2xl border p-4 shadow-sm transition",
                unread
                  ? "border-violet-200 bg-violet-50/80 dark:border-violet-800/60 dark:bg-violet-900/20"
                  : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900",
              ].join(" ")}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {unread && (
                      <span className="rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        Non lu
                      </span>
                    )}
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {date} {time && `• ${time}`}
                    </span>
                  </div>

                  <h3 className="mt-2 font-bold text-slate-900 dark:text-white">
                    {message.subject || "Sans objet"}
                  </h3>

                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    De :{" "}
                    <span className="font-semibold">
                      {message.Sender?.nom} {message.Sender?.prenom}
                    </span>
                  </p>

                  <div
                    className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600 dark:text-slate-300"
                    dangerouslySetInnerHTML={{ __html: message.content || "" }}
                  />
                </div>

                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={() => openMessage(message)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                    title="Consulter"
                  >
                    <FaEye />
                  </button>

                  <button
                    type="button"
                    onClick={() => deleteMessage(message.id)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-200"
                    title="Supprimer"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <DashboardDrawer
        open={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
        title={selectedMessage?.subject || "Message"}
        subtitle={
          selectedMessage
            ? `De ${selectedMessage.Sender?.nom || ""} ${
                selectedMessage.Sender?.prenom || ""
              }`
            : ""
        }
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setSelectedMessage(null)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              Fermer
            </button>
            <button
              type="button"
              onClick={() => navigate(DASHBOARD_ROUTES.messaging)}
              className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700"
            >
              Répondre / ouvrir la messagerie
            </button>
          </div>
        }
      >
        {selectedMessage && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-900">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                Expéditeur
              </p>
              <p className="mt-1 font-bold text-slate-900 dark:text-white">
                {selectedMessage.Sender?.nom} {selectedMessage.Sender?.prenom}
              </p>
            </div>

            <div
              className="prose prose-sm max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: selectedMessage.content || "" }}
            />
          </div>
        )}
      </DashboardDrawer>
    </>
  );
}